import { Camera, Crown, Music, Palette, ScrollText, Sparkles } from "lucide-react";
import { SITE } from "@/config/site";
import { ChapterHeading } from "@/components/ChapterHeading";
import { Reveal } from "@/components/Reveal";
import "@/styles/theme-v2.css";

const ICONS = {
  music: Music,
  sparkles: Sparkles,
  scroll: ScrollText,
  crown: Crown,
  camera: Camera,
  palette: Palette,
};

// ============================================================================
// CHAPTER 02 — EVENING HIGHLIGHTS. Same six cards, same copy, same icons —
// this only adds a cinematic band between the heading and the grid, so the
// section reads as more than an icon list. The band is a crop of the site's
// own official poster artwork (frontend/public/poster.jpg, already used in
// the Hero) showing its silhouetted qawwali-troupe illustration with no text
// in frame — not a new or invented image.
// ============================================================================

export function Highlights() {
  return (
    <section id="highlights" data-testid="highlights-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28 lg:py-36">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -right-24 top-0 h-[420px] w-[420px] rounded-full bg-[#AA7C11]/[0.07] blur-[120px]" />
      </div>
      <div className="relative mx-auto max-w-6xl">
        <ChapterHeading number="02" title="Evening Highlights" urdu="جلوے" testId="highlights-heading" />

        <Reveal delay={0.1}>
          <div className="cah-light-sweep relative mt-12 h-40 w-full overflow-hidden rounded-3xl border border-[#D4AF37]/25 bg-[#0E0C0A] sm:h-52 lg:h-64">
            <img
              src="/textures/qawwali-troupe-band.jpg"
              alt=""
              aria-hidden="true"
              data-testid="highlights-atmosphere-photo"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,5,0.15)_0%,transparent_35%,transparent_65%,rgba(8,7,5,0.55)_100%)]"
              aria-hidden="true"
            />
            <div className="pointer-events-none absolute inset-[6px] border border-[#F3E5AB]/10" aria-hidden="true" />
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SITE.highlights.map((h, i) => {
            const Icon = ICONS[h.icon] || Sparkles;
            return (
              <Reveal key={h.title} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`highlight-card-${i + 1}`}
                  className="flex h-full flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-8 transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-[#D4AF37]/55 hover:shadow-[0_24px_60px_-24px_rgba(212,175,55,0.3)]"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-[#D4AF37]/35 bg-[#D4AF37]/[0.08]">
                    <Icon className="h-5 w-5 text-[#E6CA65]" aria-hidden="true" />
                  </span>
                  <h3 className="font-display mt-6 text-xl leading-snug text-[#F7F4EF] sm:text-2xl">
                    {h.title}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-[1.8] text-[#918A7E] sm:text-base">
                    {h.text}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
